import React, { useEffect, useId, useRef, useState } from 'react';
import TimeZonePicker from '../Settings/TimeZonePicker';
import { apiService } from '../../../services/api';
import { timeSettings } from '../../../shared/datetime/timestamps';

/**
 * The zone a schedule runs in, shown and changeable: the alert, report and Push to BI
 * wizards all ask it here.
 *
 * It decides only when the schedule fires. What a run counts ("the last 30 days") is
 * always the organisation's day, whichever zone this is, and the help text says so
 * when the two differ, because an alert set for 9 PM in Vancouver in a Halifax
 * organisation runs after the organisation's midnight and counts from that new day.
 */

// What a new schedule starts on: the zone the author reads times in (their display
// zone), never a zone read silently from the browser.
export function defaultScheduleZone() {
  return timeSettings().zone || 'UTC';
}

export default function ScheduleZoneField({ value, onChange }) {
  const id = useId();
  const helpId = `${id}-help`;
  const [zones, setZones] = useState([]);
  const [status, setStatus] = useState('loading');
  const request = useRef(0);
  const zone = value || defaultScheduleZone();
  const { orgZone } = timeSettings();

  function load() {
    const mine = ++request.current;
    setStatus('loading');
    apiService.getTimeZones()
      .then((list) => {
        if (mine !== request.current) return;
        setZones(list);
        setStatus('ready');
      })
      .catch(() => { if (mine === request.current) setStatus('error'); });
  }
  useEffect(() => {
    load();
    return () => { request.current += 1; };
  }, []);

  let help = `Runs at this time in ${zone}.`;
  if (orgZone && zone === orgZone) help = 'Runs at this time in your organization’s time zone.';
  else if (orgZone) help += ` Periods such as Last 30 days still follow your organization’s day (${orgZone}).`;

  return (
    <div className="resui-field schedule-zone-field">
      <label className="resui-field-label" htmlFor={id}>Time zone</label>
      <TimeZonePicker
        id={id}
        value={zone}
        onChange={onChange}
        zones={zones}
        status={status}
        onRetry={load}
        describedBy={helpId}
      />
      <span id={helpId} className="resui-field-helper">{help}</span>
    </div>
  );
}
